// Which action an inbox row offers, and why it differs by conversation type.
//
// The SERVER decides (GET /conversations → `list_action`) and the clients render —
// same principle as `review_required` on work items: web and mobile must not drift
// apart on a rule where the wrong answer either destroys somebody else's record or
// silently does nothing. The pure rule below stays as the fallback for an older
// backend that doesn't send `list_action` yet.
//
// Nothing here is a hard delete — every action is reversible:
//  • DIRECT / CONTEXTUAL → Remove. Hides the row for THIS caller only; new activity
//    brings it back by itself.
//  • TEAM / DEPARTMENT / COMPANY → Remove. Membership of these is derived from the
//    org chart, so "leaving" is meaningless — the next resync would put the person
//    straight back. Hiding is the only thing that actually works.
//  • GROUP (a "Circle") + not OWNER → Leave. The caller goes, everyone else keeps it.
//  • GROUP + OWNER → Archive. An owner leaving their own circle would orphan it, so
//    it archives for everyone (history kept).
import type { ConversationSummary, ConversationType, MemberRole } from './types';

export type RowAction = 'remove' | 'leave' | 'archive';

/** The server's wire spelling of the same decision. */
export type ListAction = 'REMOVE' | 'LEAVE' | 'ARCHIVE';

const FROM_WIRE: Record<ListAction, RowAction> = {
  REMOVE: 'remove',
  LEAVE: 'leave',
  ARCHIVE: 'archive',
};

/** The local rule (fallback). A missing role is treated as MEMBER. */
export function resolveRowAction(type: ConversationType, myRole?: MemberRole | string | null): RowAction {
  if (type !== 'GROUP') return 'remove';
  return myRole === 'OWNER' ? 'archive' : 'leave';
}

/** What this row offers: the server's answer when it sent one, else the local rule. */
export function rowActionFor(c: Pick<ConversationSummary, 'type' | 'my_role' | 'list_action'>): RowAction {
  const fromServer = c.list_action ? FROM_WIRE[c.list_action] : undefined;
  return fromServer ?? resolveRowAction(c.type, c.my_role);
}
